import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "i18n-fe";
import useVenueDateTime from "../hooks/useVenueDateTime";

// #918 — Score Admin's proof-of-send indicator: was this ride's rider
// actually emailed their score sheet? Mirrors SheetStateChip: semantic MUI
// Chip colors only (never `primary` — dark grey, disappears in dark mode).
//
// Derived from report-api's GET /score-sheets/rides/:rideId/status response
// ({ requested, status, sheetEmailedAt, delivery, emailError }). The
// load-bearing field is `delivery` (#2844, the newest attempt's real outcome);
// `sheetEmailedAt` — it's only stamped after a real email send is CONFIRMED
// (see report-api's sheet-request.js markSheetEmailed), never on a merely
// dispatched/attempted send. That's what makes NOT_SENT/PENDING distinct from
// SENT here, rather than collapsing "requested" into "sent".
export function deriveSheetSentState(statusData) {
  // Default-parameter destructuring only covers `undefined`, not an explicit
  // `null` — and `null` is exactly what callers pass before the status fetch
  // resolves (see RideEditor's `sheetSentStatus` initial state), so this must
  // guard both.
  const { requested, status, sheetEmailedAt, delivery } = statusData || {};
  // Card #2844 — `delivery` is report-api's verdict on the NEWEST send attempt
  // (deliveryState in sheet-request.js). `sheetEmailedAt` alone cannot answer
  // "did the rider get it": it stays set from an earlier confirmed send, so a
  // resend that then failed would still read "Sent". When `delivery` is
  // present it wins outright.
  if (delivery) {
    if (delivery === "SENT") return "SENT";
    if (delivery === "FAILED") return "FAILED";
    if (delivery === "HELD") return "HELD";
    if (status === "FAILED") return "FAILED";
    return requested ? "PENDING" : "NOT_SENT";
  }
  // Payload without `delivery` (an older report-api): the #918 derivation.
  if (sheetEmailedAt) return "SENT";
  if (status === "FAILED") return "FAILED";
  if (requested) return "PENDING";
  return "NOT_SENT";
}

const SHEET_SENT_DISPLAY = {
  NOT_SENT: { color: "default", variant: "outlined", labelKey: "rideEditor.sheetSent.notSent" },
  PENDING: { color: "info", variant: "outlined", labelKey: "rideEditor.sheetSent.pending" },
  SENT: { color: "success", variant: "filled", labelKey: "rideEditor.sheetSent.sent" },
  FAILED: { color: "error", variant: "outlined", labelKey: "rideEditor.sheetSent.failed" },
  HELD: { color: "warning", variant: "outlined", labelKey: "rideEditor.sheetSent.held" },
};

export default function SheetSentChip({ statusData, size = "small", ...props }) {
  const formatVenueLocalDateTime = useVenueDateTime();
  const { t } = useTranslation("scoreadmin");
  const state = deriveSheetSentState(statusData);
  const display = SHEET_SENT_DISPLAY[state];
  const chip = (
    <Chip
      size={size}
      color={display.color}
      variant={display.variant}
      label={t(display.labelKey)}
      data-testid="sheet-sent-chip"
      data-sheet-sent-state={state}
      {...props}
    />
  );
  // Tooltips say what the state means: when a SENT one was accepted, why a
  // FAILED one failed, why a HELD one has not gone out.
  let tip = null;
  if (state === "SENT" && statusData?.sheetEmailedAt) {
    tip = t("rideEditor.sheetSent.sentAtTooltip", {
      when: formatVenueLocalDateTime(statusData.sheetEmailedAt),
    });
  } else if (state === "FAILED" && statusData?.emailError) {
    tip = t("rideEditor.sheetSent.failedTooltip", { error: statusData.emailError });
  } else if (state === "HELD") {
    tip = t("rideEditor.sheetSent.heldTooltip");
  }
  if (!tip) return chip;
  return (
    <Tooltip title={tip}>
      <span>{chip}</span>
    </Tooltip>
  );
}
